// =============================================================================
// EQuIP Program Configuration — Canonical Source of Truth
// =============================================================================
//
// This file is the single authoritative source for all EQuIP role definitions,
// quarterly schedules, UI metadata, and consistency/outcome measures.
//
// PURPOSE: Consolidates configuration that was previously scattered across
//   - NonPhysicianFramework.tsx (inline roleConfig)
//   - AwardCalculator.tsx (roleConfigs)
//   - equipStore.ts (roleNames, getMaxAward)
//   - src/data/ files
//
// IMPORTANT: This file is FRAMEWORK-AGNOSTIC (pure TypeScript data/types).
//   - No React imports
//   - No Lucide component imports
//   - Icon names are strings (consumers map to Lucide components at render time)
//
// SYNC: Synced to equip_services/src/lib/ via update.sh
// =============================================================================

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type RoleCategory = 'physician' | 'non-physician';
export type PhysicianRole = 'primary-care-physician' | 'psychiatry-physician';
export type NonPhysicianRole = 'primary-care-apc' | 'psychiatry-apc' | 'palliative-care-apc' | 'teletriage';
export type ProgramRole = PhysicianRole | NonPhysicianRole;
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
export type EmploymentStatus = 'full-time' | 'part-time';

export interface ProgramRoleConfig {
  id: ProgramRole;
  name: string;
  shortName: string;
  category: RoleCategory;
  description: string;
  /** Tailwind color token (e.g. 'eventus-teal'). Maps to bg-{color}, text-{color}, etc. */
  color: string;
  /**
   * Lucide icon name string (e.g. 'Stethoscope') — NOT a React component.
   * Consumers map this to the actual Lucide component at render time via ROLE_ICON_MAP_KEYS.
   * Keeping this a string ensures the config file stays framework-agnostic.
   */
  iconName: string;
  maxAward: {
    fullTime: number;
    perDayPartTime: number;
    /** Only set for teletriage which uses hourly part-time rate */
    perHourPartTime?: number;
  };
  hasPIMs: boolean;
  partTimeUnit: 'day' | 'hour';
}

// -----------------------------------------------------------------------------
// Non-Physician Role Configuration
// Data values match NonPhysicianFramework.tsx roleConfig (lines 92-169)
// -----------------------------------------------------------------------------

export const NON_PHYSICIAN_ROLE_CONFIG: Record<NonPhysicianRole, ProgramRoleConfig> = {
  'primary-care-apc': {
    id: 'primary-care-apc',
    name: 'Primary Care APC',
    shortName: 'PC APC',
    category: 'non-physician',
    description: 'Nurse Practitioners and Physician Assistants in primary care settings',
    color: 'eventus-teal',
    iconName: 'Stethoscope',
    maxAward: { fullTime: 3000, perDayPartTime: 600 },
    hasPIMs: true,
    partTimeUnit: 'day',
  },
  'psychiatry-apc': {
    id: 'psychiatry-apc',
    name: 'Psychiatry APC',
    shortName: 'Psych APC',
    category: 'non-physician',
    description: 'APCs focused on behavioral and mental health services',
    color: 'eventus-purple',
    iconName: 'Brain',
    maxAward: { fullTime: 3000, perDayPartTime: 600 },
    hasPIMs: true,
    partTimeUnit: 'day',
  },
  'palliative-care-apc': {
    id: 'palliative-care-apc',
    name: 'Palliative Care APC',
    shortName: 'Pall APC',
    category: 'non-physician',
    description: 'APCs providing comfort care and end-of-life support',
    color: 'rose-500',
    iconName: 'Heart',
    maxAward: { fullTime: 3000, perDayPartTime: 600 },
    hasPIMs: true,
    partTimeUnit: 'day',
  },
  teletriage: {
    id: 'teletriage',
    name: 'Teletriage',
    shortName: 'Teletriage',
    category: 'non-physician',
    description: 'Remote triage nurses handling after-hours calls',
    color: 'eventus-navy',
    iconName: 'Phone',
    maxAward: { fullTime: 1500, perDayPartTime: 300, perHourPartTime: 37.5 },
    hasPIMs: false,
    partTimeUnit: 'hour',
  },
};

// -----------------------------------------------------------------------------
// Physician Role Configuration
// -----------------------------------------------------------------------------

export const PHYSICIAN_ROLE_CONFIG: Record<PhysicianRole, ProgramRoleConfig> = {
  'primary-care-physician': {
    id: 'primary-care-physician',
    name: 'Primary Care Physician',
    shortName: 'Physician',
    category: 'physician',
    description: 'Primary care physicians measured on facility-assigned ACO patients plus quality measures',
    color: 'eventus-navy',
    iconName: 'UserRound',
    maxAward: { fullTime: 4000, perDayPartTime: 800 },
    hasPIMs: true,
    partTimeUnit: 'day',
  },
  'psychiatry-physician': {
    id: 'psychiatry-physician',
    name: 'Psychiatrist',
    shortName: 'Psychiatrist',
    category: 'physician',
    description: 'Psychiatric physicians measured on psychiatry-enrolled patients for PHQ-9',
    color: 'eventus-purple',
    iconName: 'Brain',
    maxAward: { fullTime: 4000, perDayPartTime: 800 },
    hasPIMs: true,
    partTimeUnit: 'day',
  },
};

// -----------------------------------------------------------------------------
// Combined config + helpers
// -----------------------------------------------------------------------------

export const ALL_ROLE_CONFIG: Record<ProgramRole, ProgramRoleConfig> = {
  ...PHYSICIAN_ROLE_CONFIG,
  ...NON_PHYSICIAN_ROLE_CONFIG,
};

/**
 * Look up config for any program role by ID.
 * Returns undefined if roleId is not recognized.
 */
export function getRoleConfig(roleId: ProgramRole): ProgramRoleConfig | undefined {
  return ALL_ROLE_CONFIG[roleId];
}

/**
 * Look up config for a non-physician role by ID.
 * Throws if roleId is not a valid non-physician role.
 */
export function getNonPhysicianRoleConfig(roleId: NonPhysicianRole): ProgramRoleConfig {
  return NON_PHYSICIAN_ROLE_CONFIG[roleId];
}

/**
 * Icon name keys used in role configs.
 * Consumers use this to build a Lucide icon map at render time:
 *
 *   import { Stethoscope, Brain, Heart, Phone, UserRound } from 'lucide-react';
 *   const ICON_MAP: Record<typeof ROLE_ICON_MAP_KEYS[number], React.ElementType> = {
 *     Stethoscope, Brain, Heart, Phone, UserRound,
 *   };
 *   const Icon = ICON_MAP[config.iconName as typeof ROLE_ICON_MAP_KEYS[number]];
 */
export const ROLE_ICON_MAP_KEYS = ['Stethoscope', 'Brain', 'Heart', 'Phone', 'UserRound'] as const;
export type RoleIconName = typeof ROLE_ICON_MAP_KEYS[number];
